import { verificationsApi } from '../../../data/verificationsApi';
import type { WorklistType } from '../../../data/views';
import type {
  UnassignedCasesView,
  UserAssignedCasesView,
  UserWorkloadView,
} from './workloadViews';

/**
 * Verifier workload endpoints powering the dashboard's User Assignments panel. Each computes a
 * projection over the verification worklist (owned by this backend) using verifier assignment
 * config sourced from Profiles — no server state — so they are modeled as cached queries keyed by
 * category/center. Injected into the single verifications base api; feature-owned.
 */
export const workloadEndpoints = verificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    // Compute per-verifier workload for a worklist category.
    getUserWorkload: builder.query<
      UserWorkloadView,
      { type: WorklistType; center?: string; process?: string }
    >({
      query: ({ type, center, process }) => ({
        url: '/verification/worklist/user-workload',
        method: 'GET',
        params: { type, center, process },
      }),
      providesTags: (result, error, { type, center }) => [
        { type: 'Worklist', id: `user-workload|${type}|${center ?? 'ALL'}` },
        'Worklist',
      ],
    }),

    // Resolve a single verifier's owned case ids under precedence (click-to-filter).
    getUserAssignedCases: builder.query<
      UserAssignedCasesView,
      { type: WorklistType; upn: string; center?: string; process?: string }
    >({
      query: ({ type, upn, center, process }) => ({
        url: '/verification/worklist/user-assigned-cases',
        method: 'GET',
        params: { type, upn, center, process },
      }),
      providesTags: (result, error, { type, upn, center }) => [
        { type: 'Worklist', id: `user-assigned|${type}|${upn}|${center ?? 'ALL'}` },
        'Worklist',
      ],
    }),

    // Resolve the case ids no verifier owns under precedence (Unassigned click-to-filter).
    getUnassignedCases: builder.query<
      UnassignedCasesView,
      { type: WorklistType; center?: string; process?: string }
    >({
      query: ({ type, center, process }) => ({
        url: '/verification/worklist/unassigned-cases',
        method: 'GET',
        params: { type, center, process },
      }),
      providesTags: (result, error, { type, center }) => [
        { type: 'Worklist', id: `unassigned|${type}|${center ?? 'ALL'}` },
        'Worklist',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserWorkloadQuery,
  useGetUserAssignedCasesQuery,
  useGetUnassignedCasesQuery,
} = workloadEndpoints;
